import { Directive, ElementRef, inject, computed, effect, model, OnDestroy, Renderer2, AfterViewChecked } from '@angular/core';
import { CURRENCY_parser } from 'src/services/GcTranslateParser.service';

const TEXT_NODE_TYPE = 3;

/*
* Directive to format numbers as currency. May only accept integers (also in string form).
* Does not support decimals.
* 
* Usage:
* <span currency>123</span>
* <span currency>{{product.cost}}</span>
* 
* <span [currency]="123"></span>
* <span [currency]="'123'"></span>
* <span [currency]="product.cost"></span>
*/
@Directive({
    selector:   '[currency]',
    standalone: true
})
export class VCurrencyDirective implements AfterViewChecked, OnDestroy {
    private el = inject(ElementRef);
    private renderer = inject(Renderer2);

    private effectRef = effect(() => {
        if (this.displayValue())
            this.updateNodes();
    });

    public readonly currency = model<number | string>('', {});

    private readonly value = computed(() => {
        const currency = this.currency();
        if (typeof currency === 'string')
            return currency.length > 0 ? this.parseStringToNumber(currency) : undefined;
        if (Number.isSafeInteger(currency))
            return currency;

        throw new Error('VCurrencyDirective: Value must be an integer or string representing an integer. Received: ' + currency);
    });

    private readonly displayValue = computed(() => {
        const val = this.value();
        if (typeof val === 'number')
            return CURRENCY_parser(val);
        return val;
    });

    // Update content if template binding was used instead of data binding. Exits early if content is already correct to prevent unnecessary updates.
    ngAfterViewChecked(): void {
        const content = this.getContent(this.el.nativeElement);
        if (content && this.displayValue() === content) return;
        this.currency.set(content);
    }

    ngOnDestroy(): void {
        this.effectRef.destroy();
    }

    // Sets and updates the content of (child) nodes. Span may create childtext nodes
    private updateNodes(): void {
        let nodes: NodeList = this.el.nativeElement.childNodes;
        const value = this.displayValue();

        if (!nodes.length) {
            this.setContent(this.el.nativeElement, value);
            nodes = this.el.nativeElement.childNodes;
        }

        nodes.forEach((node) => {
            if (node.nodeType !== TEXT_NODE_TYPE) return;
            this.setContent(node, value);
        });
    }

    private getContent(node: Text): string {
        return (node.textContent !== undefined && node.textContent !== null) ? node.textContent : node.data;
    }

    private setContent(node: Node, content: string): void {
        if (node.textContent !== undefined && node.textContent !== null)
            this.renderer.setProperty(node, 'textContent', content);
        else
            this.renderer.setProperty(node, 'data', content);
    }

    private parseStringToNumber(value: string): number {
        if (!value.length) return 0;
        const isNegative = value.trim().startsWith('-');
        let digits = value.replace(/\D/g, '');
        let intVal = 0;
        if (digits.length > 0)
            intVal = parseInt(digits, 10);
        return isNegative ? -intVal : intVal;
    }
}